import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAsu } from "@framework/provider-asu";
import { IUserDto, useUser } from "@framework/provider-user";
import { useApiCall } from "@framework/react-hooks";
import { enqueueSnackbar } from "notistack";
import { useIntl } from "react-intl";

import { useStructureTreeSelection } from "../pages/dashboard/tabs/multi-analysis/hooks/use-structure-tree-selection";
import { useTrainPointsCache } from "../pages/dashboard/tabs/multi-analysis/hooks/use-train-points-cache";
import {
  DEFAULT_TOOLBAR_STATE,
  DEFAULT_CELL_DISPLAY_CASCADE_3D,
  MULTI_ANALYSIS_MEASURE_TYPE,
  buildCellDisplayCascade3DKey,
  type IMultiAnalysisActiveMeasureWindow,
  type IMultiAnalysisDataMark,
  type IMultiAnalysisTimeRange,
  type IMultiAnalysisToolbarState,
  type IStructureTreePoint,
} from "../pages/dashboard/tabs/multi-analysis/types";
import { findLastDataMarkInRange } from "../pages/dashboard/tabs/multi-analysis/utils/data-marks";
import {
  MULTI_ANALYSIS_TEMPLATE_TYPE,
  normalizeAnalysisTemplateSettings,
  serializeAnalysisTemplateSettings,
  updateAnalysisTemplateCell,
  type INormalizedAnalysisTemplate,
} from "../pages/dashboard/tabs/multi-analysis/utils/analysis-template-settings";
import { draftTemplateHasInvalidFrequencyRanges } from "../pages/dashboard/tabs/multi-analysis/utils/template-filters";

export type TMultiAnalysisTemplateEditMode = "create" | "edit" | null;

interface IMultiAnalysisContextValue {
  isActive: boolean;
  selectedPointIds: ReadonlySet<number>;
  selectedPoints: IStructureTreePoint[];
  selectedPointIdsArray: number[];
  togglePoint: (point: IStructureTreePoint, checked: boolean) => void;
  setTrainPointsSelected: (points: readonly IStructureTreePoint[], checked: boolean) => void;
  pointsByTrainId: Readonly<Record<number, IStructureTreePoint[]>>;
  loadingTrainIds: ReadonlySet<number>;
  loadTrainPoints: (trainId: number) => Promise<IStructureTreePoint[]>;
  activeMeasureType: MULTI_ANALYSIS_MEASURE_TYPE | null;
  setActiveMeasureType: (measureType: MULTI_ANALYSIS_MEASURE_TYPE | null) => void;
  activeMeasureWindow: IMultiAnalysisActiveMeasureWindow | null;
  setActiveMeasureWindow: (measureWindow: IMultiAnalysisActiveMeasureWindow | null) => void;
  toolbarState: IMultiAnalysisToolbarState;
  setToolbarState: (patch: Partial<IMultiAnalysisToolbarState>) => void;
  resetToolbarState: () => void;
  getCellDisplayCascade3D: (templateId: number, cellId: string) => boolean;
  setCellDisplayCascade3D: (templateId: number, cellId: string, display3D: boolean) => void;
  frameRange: IMultiAnalysisTimeRange;
  setFrameRange: (range: IMultiAnalysisTimeRange) => void;
  resetFrameRange: (globalRange: IMultiAnalysisTimeRange) => void;
  dataMarks: IMultiAnalysisDataMark[];
  setDataMarks: (marks: IMultiAnalysisDataMark[]) => void;
  selectedDataMarks: IMultiAnalysisDataMark[];
  setSelectedDataMarks: Dispatch<SetStateAction<IMultiAnalysisDataMark[]>>;
  clearSelectedDataMarks: () => void;
  effectiveDataMarks: IMultiAnalysisDataMark[];
  selectedTemplateListId: number | null;
  setSelectedTemplateListId: (templateId: number | null) => void;
  appliedTemplateId: number | null;
  isTemplateEditMode: boolean;
  templateEditMode: TMultiAnalysisTemplateEditMode;
  draftTemplate: INormalizedAnalysisTemplate | null;
  setDraftTemplate: Dispatch<SetStateAction<INormalizedAnalysisTemplate | null>>;
  startCreateTemplate: (draft: INormalizedAnalysisTemplate) => void;
  startEditTemplate: (draft: INormalizedAnalysisTemplate) => void;
  cancelTemplateEdit: () => void;
  updateDraftTemplateCell: (
    cellId: string,
    patch: Partial<{
      measureType: INormalizedAnalysisTemplate["settings"]["cells"][number]["measureType"];
      filters: Record<string, unknown>;
    }>,
  ) => void;
  saveTemplateEdit: () => Promise<boolean>;
  deleteTemplate: () => Promise<boolean>;
  applySelectedTemplate: () => void;
}

const MultiAnalysisContext = createContext<IMultiAnalysisContextValue | null>(null);

export const MultiAnalysisProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { formatMessage } = useIntl();
  const user = useUser<IUserDto>();
  const { tab, startTimestamp, endTimestamp, selectedItem } = useAsu();
  const isActive = tab === "6";
  const prevTabRef = useRef(tab);

  const {
    selectedPointIds,
    selectedPoints: allSelectedPoints,
    togglePoint,
    setTrainPointsSelected,
    resetSelection,
  } = useStructureTreeSelection();
  const { pointsByTrainId, loadingTrainIds, loadTrainPoints } = useTrainPointsCache();

  const [activeMeasureType, setActiveMeasureType] = useState<MULTI_ANALYSIS_MEASURE_TYPE | null>(
    MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM,
  );
  const [activeMeasureWindow, setActiveMeasureWindow] = useState<IMultiAnalysisActiveMeasureWindow | null>(null);
  const [toolbarState, setToolbarStateInternal] = useState<IMultiAnalysisToolbarState>(DEFAULT_TOOLBAR_STATE);
  const [cellDisplayCascade3DByKey, setCellDisplayCascade3DByKey] = useState<Record<string, boolean>>({});
  const [frameRange, setFrameRange] = useState<IMultiAnalysisTimeRange>({
    start: startTimestamp,
    end: endTimestamp,
  });
  const [dataMarks, setDataMarks] = useState<IMultiAnalysisDataMark[]>([]);
  const [selectedDataMarks, setSelectedDataMarks] = useState<IMultiAnalysisDataMark[]>([]);
  const [selectedTemplateListId, setSelectedTemplateListId] = useState<number | null>(null);
  const [appliedTemplateId, setAppliedTemplateId] = useState<number | null>(null);
  const [templateEditMode, setTemplateEditMode] = useState<TMultiAnalysisTemplateEditMode>(null);
  const [draftTemplate, setDraftTemplate] = useState<INormalizedAnalysisTemplate | null>(null);
  const draftTemplateSnapshotRef = useRef<INormalizedAnalysisTemplate | null>(null);
  const isTemplateEditMode = templateEditMode !== null;

  const selectedTrainId = useMemo(() => {
    if (!selectedItem || !("TrainId" in selectedItem)) {
      return null;
    }

    const trainId = Number(selectedItem.TrainId);

    return Number.isFinite(trainId) ? trainId : null;
  }, [selectedItem]);

  const selectedPoints = useMemo(
    () => (selectedTrainId === null ? [] : allSelectedPoints.filter(point => point.trainId === selectedTrainId)),
    [allSelectedPoints, selectedTrainId],
  );
  const selectedPointIdsArrayForActiveTrain = useMemo(() => selectedPoints.map(point => point.id), [selectedPoints]);
  const previousSelectedTrainIdRef = useRef<number | null>(selectedTrainId);

  const setToolbarState = useCallback((patch: Partial<IMultiAnalysisToolbarState>) => {
    setToolbarStateInternal(prev => ({ ...prev, ...patch }));
  }, []);

  const resetToolbarState = useCallback(() => {
    setToolbarStateInternal(DEFAULT_TOOLBAR_STATE);
  }, []);

  const getCellDisplayCascade3D = useCallback(
    (templateId: number, cellId: string): boolean => {
      const key = buildCellDisplayCascade3DKey(templateId, cellId);

      return cellDisplayCascade3DByKey[key] ?? DEFAULT_CELL_DISPLAY_CASCADE_3D;
    },
    [cellDisplayCascade3DByKey],
  );

  const setCellDisplayCascade3D = useCallback((templateId: number, cellId: string, display3D: boolean) => {
    const key = buildCellDisplayCascade3DKey(templateId, cellId);

    setCellDisplayCascade3DByKey(prev => ({ ...prev, [key]: display3D }));
  }, []);

  const resetCellDisplayCascade3D = useCallback(() => {
    setCellDisplayCascade3DByKey({});
  }, []);

  const resetFrameRange = useCallback((globalRange: IMultiAnalysisTimeRange) => {
    setFrameRange({
      start: globalRange.start,
      end: globalRange.end,
    });
  }, []);

  const clearSelectedDataMarks = useCallback(() => {
    setSelectedDataMarks([]);
  }, []);

  const { fn: saveTemplateRequest } = useApiCall(
    async (
      api,
      template: INormalizedAnalysisTemplate,
      mode: Exclude<TMultiAnalysisTemplateEditMode, null>,
    ): Promise<{ idAnalysisTemplate?: number }> => {
      const payload = {
        name: template.name.trim(),
        isPublic: template.isPublic,
        settings: serializeAnalysisTemplateSettings(
          normalizeAnalysisTemplateSettings({
            layout: template.settings.layout,
            cells: template.settings.cells.map(cell => ({
              row: cell.row,
              column: cell.column,
              viewType: cell.measureType,
              filters: cell.filters,
            })),
          }),
        ),
        ...(user.isAdmin() ? { isStandard: template.type === MULTI_ANALYSIS_TEMPLATE_TYPE.STANDARD } : {}),
      };

      if (mode === "create") {
        return (await api.fetchJson({
          url: "/multi-analysis/templates",
          method: "POST",
          data: payload,
        })) as { idAnalysisTemplate?: number };
      }

      return (await api.fetchJson({
        url: `/multi-analysis/templates/${template.idAnalysisTemplate}`,
        method: "PUT",
        data: payload,
      })) as { idAnalysisTemplate?: number };
    },
    { success: false, error: true },
  );

  const { fn: deleteTemplateRequest } = useApiCall(
    async (api, templateId: number): Promise<void> => {
      await api.fetchJson({
        url: `/multi-analysis/templates/${templateId}`,
        method: "DELETE",
      });
    },
    { success: false, error: true },
  );

  const resetTemplateEditingState = useCallback(() => {
    setTemplateEditMode(null);
    setDraftTemplate(null);
    draftTemplateSnapshotRef.current = null;
  }, []);

  const startCreateTemplate = useCallback(
    (draft: INormalizedAnalysisTemplate) => {
      draftTemplateSnapshotRef.current = null;
      setDraftTemplate(draft);
      setTemplateEditMode("create");
      setActiveMeasureWindow(null);
      clearSelectedDataMarks();
      setDataMarks([]);
    },
    [clearSelectedDataMarks],
  );

  const startEditTemplate = useCallback(
    (draft: INormalizedAnalysisTemplate) => {
      draftTemplateSnapshotRef.current = draft;
      setDraftTemplate(draft);
      setTemplateEditMode("edit");
      setActiveMeasureWindow(null);
      clearSelectedDataMarks();
      setDataMarks([]);
    },
    [clearSelectedDataMarks],
  );

  const cancelTemplateEdit = useCallback(() => {
    resetTemplateEditingState();
  }, [resetTemplateEditingState]);

  const updateDraftTemplateCell = useCallback(
    (
      cellId: string,
      patch: Partial<{
        measureType: INormalizedAnalysisTemplate["settings"]["cells"][number]["measureType"];
        filters: Record<string, unknown>;
      }>,
    ) => {
      setDraftTemplate(prev => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          settings: updateAnalysisTemplateCell(prev.settings, cellId, patch),
        };
      });
    },
    [],
  );

  const saveTemplateEdit = useCallback(async (): Promise<boolean> => {
    if (!draftTemplate || !templateEditMode) {
      return false;
    }

    if (!draftTemplate.name.trim()) {
      enqueueSnackbar(formatMessage({ id: "pages.multiAnalysis.templateEditor.nameRequired" }), { variant: "error" });
      return false;
    }

    if (draftTemplateHasInvalidFrequencyRanges(draftTemplate.settings.cells)) {
      enqueueSnackbar(formatMessage({ id: "pages.multiAnalysis.templateEditor.frequencyRangeInvalid" }), {
        variant: "error",
      });
      return false;
    }

    try {
      const response = (await saveTemplateRequest(undefined, draftTemplate, templateEditMode)) as
        | { idAnalysisTemplate?: number }
        | undefined;
      const savedTemplateId = Number(response?.idAnalysisTemplate ?? draftTemplate.idAnalysisTemplate);

      if (Number.isFinite(savedTemplateId) && savedTemplateId > 0) {
        setAppliedTemplateId(savedTemplateId);
        setSelectedTemplateListId(savedTemplateId);
      }

      resetTemplateEditingState();
      enqueueSnackbar(formatMessage({ id: "pages.multiAnalysis.templateEditor.saved" }), { variant: "success" });
      return true;
    } catch {
      return false;
    }
  }, [draftTemplate, formatMessage, resetTemplateEditingState, saveTemplateRequest, templateEditMode]);

  const deleteTemplate = useCallback(async (): Promise<boolean> => {
    if (!selectedTemplateListId) {
      return false;
    }

    try {
      await deleteTemplateRequest(undefined, selectedTemplateListId);
      if (appliedTemplateId === selectedTemplateListId) {
        setAppliedTemplateId(null);
      }
      setSelectedTemplateListId(null);
      enqueueSnackbar(formatMessage({ id: "pages.multiAnalysis.templateEditor.deleted" }), { variant: "success" });
      return true;
    } catch {
      return false;
    }
  }, [appliedTemplateId, deleteTemplateRequest, formatMessage, selectedTemplateListId]);

  const applySelectedTemplate = useCallback(() => {
    if (!selectedTemplateListId) {
      return;
    }

    setAppliedTemplateId(selectedTemplateListId);
    setActiveMeasureWindow(null);
    clearSelectedDataMarks();
    setDataMarks([]);
    resetFrameRange({ start: startTimestamp, end: endTimestamp });
  }, [clearSelectedDataMarks, endTimestamp, resetFrameRange, selectedTemplateListId, startTimestamp]);

  useEffect(() => {
    const globalRange = { start: startTimestamp, end: endTimestamp };
    resetFrameRange(globalRange);
    clearSelectedDataMarks();
    setDataMarks([]);
  }, [clearSelectedDataMarks, endTimestamp, resetFrameRange, startTimestamp]);

  useEffect(() => {
    if (!isActive) {
      previousSelectedTrainIdRef.current = selectedTrainId;
      return;
    }

    if (previousSelectedTrainIdRef.current === selectedTrainId) {
      return;
    }

    previousSelectedTrainIdRef.current = selectedTrainId;
    clearSelectedDataMarks();
    setDataMarks([]);
    setToolbarState({
      orbitFilterOpen: false,
      orbitAppliedBins: [],
      orbitAppliedBandpassHz: null,
      signalAppliedHarmonics: [],
      signalAppliedBandpassHz: null,
    });
  }, [clearSelectedDataMarks, isActive, selectedTrainId, setToolbarState]);

  const effectiveDataMarks = useMemo(() => {
    if (selectedDataMarks.length > 0) {
      return selectedDataMarks;
    }

    const lastMark = findLastDataMarkInRange(dataMarks, frameRange);

    return lastMark ? [lastMark] : [];
  }, [dataMarks, frameRange, selectedDataMarks]);

  useEffect(() => {
    if (isActive && prevTabRef.current !== "6") {
      resetSelection();
      setActiveMeasureWindow(null);
      setActiveMeasureType(MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM);
      resetToolbarState();
      resetCellDisplayCascade3D();
      clearSelectedDataMarks();
      setDataMarks([]);
      resetFrameRange({ start: startTimestamp, end: endTimestamp });
      resetTemplateEditingState();
      setSelectedTemplateListId(null);
      setAppliedTemplateId(null);
    }

    prevTabRef.current = tab;
  }, [
    clearSelectedDataMarks,
    endTimestamp,
    isActive,
    resetFrameRange,
    resetSelection,
    resetTemplateEditingState,
    resetToolbarState,
    resetCellDisplayCascade3D,
    startTimestamp,
    tab,
  ]);

  const value = useMemo(
    () => ({
      isActive,
      selectedPointIds,
      selectedPoints,
      selectedPointIdsArray: selectedPointIdsArrayForActiveTrain,
      togglePoint,
      setTrainPointsSelected,
      pointsByTrainId,
      loadingTrainIds,
      loadTrainPoints,
      activeMeasureType,
      setActiveMeasureType,
      activeMeasureWindow,
      setActiveMeasureWindow,
      toolbarState,
      setToolbarState,
      resetToolbarState,
      getCellDisplayCascade3D,
      setCellDisplayCascade3D,
      frameRange,
      setFrameRange,
      resetFrameRange,
      dataMarks,
      setDataMarks,
      selectedDataMarks,
      setSelectedDataMarks,
      clearSelectedDataMarks,
      effectiveDataMarks,
      selectedTemplateListId,
      setSelectedTemplateListId,
      appliedTemplateId,
      isTemplateEditMode,
      templateEditMode,
      draftTemplate,
      setDraftTemplate,
      startCreateTemplate,
      startEditTemplate,
      cancelTemplateEdit,
      updateDraftTemplateCell,
      saveTemplateEdit,
      deleteTemplate,
      applySelectedTemplate,
    }),
    [
      isActive,
      selectedPointIds,
      selectedPoints,
      selectedPointIdsArrayForActiveTrain,
      togglePoint,
      setTrainPointsSelected,
      pointsByTrainId,
      loadingTrainIds,
      loadTrainPoints,
      activeMeasureType,
      activeMeasureWindow,
      toolbarState,
      setToolbarState,
      resetToolbarState,
      getCellDisplayCascade3D,
      setCellDisplayCascade3D,
      frameRange,
      resetFrameRange,
      dataMarks,
      selectedDataMarks,
      clearSelectedDataMarks,
      effectiveDataMarks,
      selectedTemplateListId,
      appliedTemplateId,
      isTemplateEditMode,
      templateEditMode,
      draftTemplate,
      startCreateTemplate,
      startEditTemplate,
      cancelTemplateEdit,
      updateDraftTemplateCell,
      saveTemplateEdit,
      deleteTemplate,
      applySelectedTemplate,
    ],
  );

  return <MultiAnalysisContext.Provider value={value}>{children}</MultiAnalysisContext.Provider>;
};

export const useMultiAnalysis = (): IMultiAnalysisContextValue => {
  const context = useContext(MultiAnalysisContext);

  if (!context) {
    throw new Error("useMultiAnalysis must be used within MultiAnalysisProvider");
  }

  return context;
};

export const useOptionalMultiAnalysis = (): IMultiAnalysisContextValue | null => useContext(MultiAnalysisContext);
